import { useRef, useState } from 'react';
import { useToggle } from 'react-use';
import cn from 'classnames';
import { MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { HrSystemConflictCard } from '@lp/features/Scheduler/components';
import { sShiftTypeNames } from '@lp/features/Scheduler/SchedulerSurgeons/constants';
import { SShiftCellDropdown } from '@lp/features/Scheduler/SchedulerSurgeons/components';
import { getSShiftSetData, getSShiftDropdownOptionIconColor } from '@lp/features/Scheduler/SchedulerSurgeons/utils';
import {
  ScheduleHrSystemConflictType,
  SResolveHrSystemConflict,
  SResourceSShiftTypeHrSystemConflict,
  SShift,
  SShiftType,
} from '@/api/apis/resources/schedule';

type SHrSystemConflictCardProps = {
  conflict: SResourceSShiftTypeHrSystemConflict;
  onConflictResolve: (resolveData: SResolveHrSystemConflict) => void;
};

export const SHrSystemConflictCard = ({ conflict, onConflictResolve }: SHrSystemConflictCardProps) => {
  const { conflictId, type, resource, hrSystemShiftType } = conflict;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, toggleIsOpen] = useToggle(false);
  const [shiftType, setShiftType] = useState<SShiftType>();
  const shiftPayload = shiftType ? { shiftType } : undefined;
  const textColor = getSShiftDropdownOptionIconColor(shiftType);
  const isRemoved = type === ScheduleHrSystemConflictType.HrSystemShiftTypeRemoved;

  const onApply = () => {
    const newShiftType = isRemoved ? shiftType : hrSystemShiftType;
    if (newShiftType) {
      const setShiftData = getSShiftSetData({ shiftType: newShiftType }, resource, resource.regionId);
      onConflictResolve({ conflictId, setShiftData });
    }
  };

  return (
    <HrSystemConflictCard conflict={conflict} isApplyDisabled={isRemoved && !shiftType} onApply={onApply}>
      {isRemoved ? (
        <>
          <button
            ref={triggerRef}
            className={cn('flex text-m-r hover:underline', shiftType ? textColor : 'text-base-25')}
            onClick={toggleIsOpen}
          >
            <span>{shiftType ? sShiftTypeNames[shiftType] : 'No shift'}</span>
            <MdOutlineKeyboardArrowDown size={18} className={cn({ 'rotate-180': isOpen })} />
          </button>
          {isOpen && (
            <SShiftCellDropdown
              triggerRef={triggerRef}
              title="Pick Shift"
              isPlacementBottom
              isOpen={isOpen}
              onOpenChange={toggleIsOpen}
              shiftData={shiftPayload as SShift} // Can omit other fields in this case
              onShiftChange={setShiftType}
              canReset
            />
          )}
        </>
      ) : (
        <span>{sShiftTypeNames[hrSystemShiftType]}</span>
      )}
    </HrSystemConflictCard>
  );
};
